/**
 * JourneyVesselStack — the 3D scene composer.
 *
 * Owns the <Canvas>, environment lighting, camera, background, and the
 * placement of PresenceCube and JourneyVessel(s). Called a "Stack"
 * because a boutique with multiple guests in flight will one day render
 * multiple vessels in parallel — this component is the seam where that
 * change lands. For the prototype, exactly one vessel is drawn.
 *
 * The scene is intentionally quiet: neutral background, soft key light,
 * a large area light for the transmission material to catch, and a
 * procedural environment made from Lightformers so there's no external
 * asset download.
 */
import React, { Suspense } from "react";
import { Canvas } from "@react-three/fiber";
import { Environment, Lightformer } from "@react-three/drei";
import PresenceCube from "./PresenceCube";
import JourneyVessel from "./JourneyVessel";
import JourneyStageContent from "./JourneyStageContent";

/**
 * Business-state filter: content is visible only when the vessel is
 * physically solid *and* a business stage has been declared.
 */
function isContentVisible(phase, businessStage) {
  if (businessStage === "idle") return false;
  return (
    phase === "solidifying" ||
    phase === "stable" ||
    phase === "softening"
  );
}

export default function JourneyVesselStack({ transition, businessStage, guest }) {
  const contentVisible = isContentVisible(transition.phase, businessStage);
  const displayStage =
    businessStage === "idle" ? "arrival" : businessStage;

  return (
    <div
      style={{ width: "100%", height: "100%", position: "relative" }}
      data-testid="journey-vessel-stack"
    >
      <Canvas
        dpr={[1, 2]}
        gl={{
          antialias: true,
          alpha: true,
          powerPreference: "high-performance",
          preserveDrawingBuffer: false,
        }}
        camera={{ position: [0, 0.7, 6.8], fov: 32 }}
        style={{ background: "transparent" }}
      >
        <color attach="background" args={["#efeae2"]} />
        <ambientLight intensity={0.4} />
        <directionalLight position={[3, 4, 3]} intensity={1.0} castShadow={false} />
        <directionalLight position={[-3, 2, -2]} intensity={0.35} />
        <pointLight position={[0, 3, 4]} intensity={0.35} color="#fff2df" />

        <Suspense fallback={null}>
          {/*
            Backdrop plane — a soft-toned wall a few units behind the
            action. Gives the transmission material something to refract
            so the glass reads as clear rather than as flat white.
          */}
          <mesh position={[0, 0, -1.8]}>
            <planeGeometry args={[14, 8]} />
            <meshStandardMaterial color="#e6ddcd" roughness={0.9} metalness={0} />
          </mesh>

          {/*
            Two very quiet ribbons on the backdrop. Low-contrast — they
            barely register as themselves, but the light passing through
            the cube and vessel bends visibly against them. This is what
            makes the glass read as glass instead of ceramic.
          */}
          <mesh position={[0, -0.7, -1.6]}>
            <planeGeometry args={[14, 0.6]} />
            <meshStandardMaterial color="#d4c9b3" roughness={0.9} metalness={0} />
          </mesh>
          <mesh position={[0, 1.15, -1.6]}>
            <planeGeometry args={[14, 0.32]} />
            <meshStandardMaterial color="#dfd4bf" roughness={0.9} metalness={0} />
          </mesh>

          {/*
            Procedural environment for the transmission material to
            refract. No external asset download — all Lightformer planes.
          */}
          <Environment resolution={256} frames={1} background={false}>
            <Lightformer
              form="rect"
              intensity={3.2}
              position={[0, 4, 3]}
              rotation={[-Math.PI / 3, 0, 0]}
              scale={[8, 3, 1]}
              color="#ffffff"
            />
            <Lightformer
              form="rect"
              intensity={1.6}
              position={[-4, 1, 2]}
              rotation={[0, Math.PI / 2, 0]}
              scale={[4, 3, 1]}
              color="#fff4e6"
            />
            <Lightformer
              form="rect"
              intensity={1.4}
              position={[4, 1, 2]}
              rotation={[0, -Math.PI / 2, 0]}
              scale={[4, 3, 1]}
              color="#eef0ff"
            />
            <Lightformer
              form="rect"
              intensity={0.7}
              position={[0, -3, 2]}
              rotation={[Math.PI / 2, 0, 0]}
              scale={[6, 3, 1]}
              color="#ffffff"
            />
          </Environment>

          <PresenceCube transition={transition} />
          <JourneyVessel transition={transition} />

          <JourneyStageContent
            visible={contentVisible}
            stage={displayStage}
            guest={guest}
            position={transition.SCENE.vesselPosition}
          />
        </Suspense>
      </Canvas>
    </div>
  );
}
